<template>
  <component :is="tourLayout">
    <div class="w-full">
      <header
        class="w-full max-w-7xl mx-auto px-3 xl:px-0 my-4 flex flex-col gap-2"
      >
        <h1 class="text-3xl font-black">{{ copy.heading }}</h1>
        <p v-if="copy.intro" class="max-w-4xl text-gray-300">
          {{ copy.intro }}
        </p>
      </header>

      <SectionContainer v-if="tags.length" id="all-tags">
        <template #header>
          <h2 class="text-2xl">All tags</h2>
        </template>
        <ul
          class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-1"
        >
          <li v-for="tag in tags" :key="`tag-${tag.slug}`">
            <NuxtLink :to="`/tags/${tag.slug}`" class="hover:underline">
              {{ tag.label }}
            </NuxtLink>
            <span class="text-gray-400 text-sm">
              {{ tag.sceneCount.toLocaleString("en-US") }}
            </span>
          </li>
        </ul>
      </SectionContainer>
    </div>
  </component>
</template>

<script setup>
  // Every tag on the site with a hub page, the most-used first, so the grid
  // reads in the order the intro promises.
  defineOptions({
    name: "TagsPage",
  })

  useRedirectInAgeRestrictedArea()

  const currentSite = inject("currentSite")
  const tourLayout = useTourLayout(currentSite.value)
  const route = useRoute()

  // `totals` wraps the tags with the site's public scene count. An older
  // backend answers with the bare array, and the copy then leaves the count
  // out.
  const { data } = await useApi("/api/tags", { query: { totals: true } })

  const tags = computed(() => {
    const list = Array.isArray(data.value) ? data.value : data.value?.tags
    return [...(list ?? [])].sort(
      (a, b) => b.sceneCount - a.sceneCount || a.label.localeCompare(b.label),
    )
  })

  // The same name useSiteSeo gives `{{ siteName }}` in every other title.
  const brand = computed(() => {
    const site = currentSite.value.alias || currentSite.value
    return site.seoSiteName || currentSite.value.name
  })

  const copy = computed(() =>
    tagsIndexCopy({
      brand: brand.value,
      niche: siteNiche(currentSite.value.host),
      tags: tags.value,
      totalScenes: Array.isArray(data.value)
        ? null
        : (data.value?.totalScenes ?? null),
    }),
  )

  useSiteSeo(
    {
      defaultTitle: "{{{ title }}}",
      defaultDescription: "{{{ description }}}",
      title: copy.value.title,
      description: copy.value.description,
    },
    {},
    { siteTemplates: false },
  )

  const origin = publicSiteOrigin(useRequestURL().href)
  const schema = computed(() =>
    buildCollectionPageSchema({
      origin,
      url: `${origin}${route.path}`,
      name: copy.value.heading,
      description: copy.value.description,
      trail: [{ name: currentSite.value.name, path: "/" }, { name: "Tags" }],
      items: tags.value.map((tag) => ({
        path: `/tags/${tag.slug}`,
        name: tag.label,
      })),
    }),
  )

  useHead({
    script: computed(() =>
      schema.value
        ? [
            {
              key: "schema-CollectionPage",
              type: "application/ld+json",
              innerHTML: serializeJsonLd(schema.value),
            },
          ]
        : [],
    ),
  })
</script>
